"use client";

import { useMemo, useState } from "react";
import { createTranslator } from "@/lib/localization";

type Props = {
  activeLanguage: string;
  texts?: Record<string, string> | null;
  companyName?: string;
  phoneNumber?: string;
  whatsappNumber?: string;
  email?: string;
  address?: string;
};

function toWhatsappLink(value?: string) {
  if (!value) return undefined;
  const normalized = value.replace(/[^\d+]/g, "");
  const digitsOnly = normalized.replace(/\D/g, "");
  return digitsOnly ? `https://wa.me/${digitsOnly}` : undefined;
}

export function ContactTemplateClient({
  texts,
  companyName,
  phoneNumber,
  whatsappNumber,
  email,
  address,
}: Props) {
  const t = createTranslator(texts ?? {});
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    gsm: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const whatsappHref = useMemo(() => toWhatsappLink(whatsappNumber), [whatsappNumber]);

  function updateField<K extends keyof typeof form>(key: K, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitted(true);
  }

  return (
    <section style={styles.section}>
      <div style={styles.grid}>
        <div style={styles.card}>
          <h2 style={styles.title}>{t("contact.formTitle")}</h2>
          <form onSubmit={handleSubmit} style={styles.form}>
            <label style={styles.label}>{t("contact.fullName")}</label>
            <input
              style={styles.input}
              value={form.fullName}
              onChange={(e) => updateField("fullName", e.target.value)}
              required
            />

            <label style={styles.label}>{t("contact.email")}</label>
            <input
              type="email"
              style={styles.input}
              value={form.email}
              onChange={(e) => updateField("email", e.target.value)}
              required
            />

            <label style={styles.label}>{t("contact.gsm")}</label>
            <input
              style={styles.input}
              value={form.gsm}
              onChange={(e) => updateField("gsm", e.target.value)}
              required
            />

            <label style={styles.label}>{t("contact.message")}</label>
            <textarea
              style={styles.textarea}
              value={form.message}
              onChange={(e) => updateField("message", e.target.value)}
              required
            />

            <button type="submit" style={styles.button}>
              {t("contact.send")}
            </button>

            {submitted ? (
              <p style={styles.success}>
                {t("contact.successMessage")}
              </p>
            ) : null}
          </form>
        </div>

        <div style={styles.card}>
          <h2 style={styles.title}>{t("contact.infoTitle")}</h2>
          <div style={styles.infoList}>
            <InfoRow label={t("contact.companyLabel")} value={companyName} />
            <InfoRow label={t("contact.phoneLabel")} value={phoneNumber} href={phoneNumber ? `tel:${phoneNumber}` : undefined} />
            <InfoRow label={t("contact.whatsappLabel")} value={whatsappNumber} href={whatsappHref} />
            <InfoRow label={t("contact.email")} value={email} href={email ? `mailto:${email}` : undefined} />
            <InfoRow label={t("contact.addressLabel")} value={address} />
          </div>
        </div>
      </div>
    </section>
  );
}

function InfoRow({ label, value, href }: { label: string; value?: string; href?: string }) {
  return (
    <div style={styles.infoRow}>
      <span style={styles.infoLabel}>{label}</span>
      {value ? (
        href ? (
          <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" style={styles.link}>
            {value}
          </a>
        ) : (
          <span style={styles.infoValue}>{value}</span>
        )
      ) : (
        <span style={styles.muted}>-</span>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  section: { marginTop: 16 },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: 16,
  },
  card: {
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: "1rem",
    background: "#fff",
  },
  title: { margin: "0 0 0.9rem", fontSize: "1.1rem", color: "#0f172a", fontWeight: 700 },
  form: { display: "grid", gap: 8 },
  label: { fontSize: "0.85rem", color: "#334155", fontWeight: 600 },
  input: {
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    padding: "0.55rem 0.7rem",
    fontSize: "0.9rem",
  },
  textarea: {
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    padding: "0.55rem 0.7rem",
    minHeight: 120,
    resize: "vertical",
    fontSize: "0.9rem",
  },
  button: {
    marginTop: 6,
    border: "none",
    borderRadius: 8,
    background: "#2563eb",
    color: "#fff",
    fontWeight: 700,
    padding: "0.6rem 0.9rem",
    cursor: "pointer",
  },
  success: { margin: "0.4rem 0 0", color: "#15803d", fontSize: "0.85rem", fontWeight: 600 },
  infoList: { display: "grid", gap: 10 },
  infoRow: {
    display: "grid",
    gridTemplateColumns: "120px 1fr",
    gap: 10,
    alignItems: "start",
    fontSize: "0.9rem",
  },
  infoLabel: { color: "#475569", fontWeight: 600 },
  infoValue: { color: "#0f172a" },
  muted: { color: "#94a3b8" },
  link: { color: "#2563eb", textDecoration: "none", wordBreak: "break-word" },
};
